import WolfButton from '@components/ui/WolfButton';
import WolfInput from '@components/ui/WolfInput';
import { graphQlClient, updateHeaders } from '@config/graphqlClient';
import OrganizationsGraphQL from '@graphql/organization.queries';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { createOrUpdateOrganizationRequest } from '@models/organization.model';
import { getSelfWithOrganizationRequest } from '@models/user.model';
import { Box, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { setUserDetails, setUserOrganizations } from '@redux/reducers/user.reducer';
import { RootState } from '@redux/store';
import React, { useState } from 'react';

interface NoOrganizationScreenProps {}

const NoOrganizationScreen: React.FC<NoOrganizationScreenProps> = () => {
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');
  const { email: currentUserEmail } = useAppSelector((state: RootState) => state.user);

  const [organizationName, setOrganizationName] = useState('');
  const [organizationDescription, setOrganizationDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreateOrganization = async () => {
    if (!organizationName.trim()) {
      dispatch(
        appendActionMessage({
          message: 'Organization name is required',
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    if (!currentUserEmail) {
      dispatch(
        appendActionMessage({
          message: 'User email not available',
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    setSaving(true);
    try {
      const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
        OrganizationsGraphQL.mutations.createOrganizationByEmail,
        {
          organization: {
            name: organizationName.trim(),
            ownerEmail: currentUserEmail,
            description: organizationDescription.trim() || undefined,
          },
        },
      );

      if ((data as any)?.createOrganizationByEmail?.organizationId) {
        dispatch(
          appendActionMessage({
            message: 'Organization created successfully! Redirecting...',
            type: SnackType.SUCCESS,
          }),
        );

        // Refresh user data to get the new organization
        const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
          UserGraphQL.queries.getSelfWithOrganization,
        );

        const user = userData.getSelfWithOrganization.user;
        const role = userData.getSelfWithOrganization.role;
        const organization = userData.getSelfWithOrganization.organization;
        const organizations = userData.getSelfWithOrganization.organizations || [];

        // Set organization data - user should now have access to the app
        if (organization) {
          dispatch(setOrganizationData(organization));
        }
        dispatch(setUserDetails({ ...user, role }));
        dispatch(setUserOrganizations(organizations));

        // Update headers for the new organization context
        if (organization?.organizationId) {
          updateHeaders({
            'X-Organization-Id': organization.organizationId,
          });
        }

        // User will now have access to the app
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="min-h-screen bg-backgroundUI flex items-center justify-center p-4">
      <Box className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8">
        <Box className="mb-6 text-center">
          <Typography variant="h3" className="!mb-4 text-primary">
            Welcome to Wolf
          </Typography>

          <Typography variant="h4" className="!mb-4">
            Create Your Organization
          </Typography>

          <Typography variant="body1" color="textSecondary" className="!mb-6">
            To get started with Wolf, you need to be a member of at least one organization. Create your organization
            below to access all of Wolf's powerful business process management features.
          </Typography>
        </Box>

        <Box className="mb-6">
          {/* Inline Organization Creation Form */}
          <Box className="space-y-4">
            <Box>
              <WolfInput
                fullWidth
                label={localeCommon['organization'] + ' ' + localeCommon['name']}
                placeholder="Enter your organization name"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                required
              />
            </Box>

            <Box>
              <WolfInput
                fullWidth
                label={localeCommon['organization'] + ' ' + localeCommon['description']}
                placeholder="Describe your organization (optional)"
                value={organizationDescription}
                onChange={(e) => setOrganizationDescription(e.target.value)}
              />
            </Box>

            <WolfButton
              color="primary"
              onClick={handleCreateOrganization}
              disabled={saving || !organizationName.trim()}
              className="!w-full !py-3 !text-lg !mt-6">
              {saving ? 'Creating Organization...' : 'Create Organization'}
            </WolfButton>
          </Box>
        </Box>

        <Typography variant="body2" color="textSecondary" className="!mt-4 text-center">
          Already have an organization? Contact your administrator to be added as a member.
        </Typography>
      </Box>
    </Box>
  );
};

export default NoOrganizationScreen;
